import { motion } from "framer-motion";
import { Target, Users, Trophy, IndianRupee } from "lucide-react";
import { SectionHead, Reveal } from "../Reveal";
import { IMAGES } from "../../data/site";

const VALUES = [
  { icon: IndianRupee, title: "Affordable, Not Cheap", desc: "Big institutes charge lakhs for recorded batches. We deliver better quality — live engineers, real labs — at a fraction of the market price." },
  { icon: Trophy, title: "Results-Oriented", desc: "We measure success in one unit only: your job offer. Every course ends with mock interviews, resume polish and placement push." },
  { icon: Users, title: "The TAC Engineer Concept", desc: "Our trainers are working TAC/escalation engineers — the people companies call when things break. You learn from real production cases, not slides." },
  { icon: Target, title: "Real-World Knowledge", desc: "Every lab mirrors an actual enterprise ticket or incident. You practise what MNC interviews and jobs actually demand." },
];

export const AboutUs = () => (
  <section id="about" data-testid="about-us-section" className="relative py-20 sm:py-28 overflow-hidden">
    <div className="absolute -top-32 right-0 w-[400px] h-[400px] rounded-full bg-blue-700/15 blur-[130px]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHead
        eyebrow="About Us"
        title={
          <>
            Quality Training Shouldn't Cost a Fortune.{" "}
            <span className="text-gradient-orange">We're Proving It.</span>
          </>
        }
      />

      <div className="grid lg:grid-cols-2 gap-12 items-center mb-14">
        <Reveal>
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
            LearnWithTAC started with a simple frustration — IT training in the market was either{" "}
            <span className="text-orange-400 font-semibold">overpriced</span> or{" "}
            <span className="text-orange-400 font-semibold">low quality</span>. Recorded batches sold as "live
            classes", trainers who never worked in the industry, and labs that exist only in screenshots.
          </p>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
            We built the opposite: an affordable learning platform where every student trains with a{" "}
            <span className="text-sky-400 font-semibold">working TAC-level engineer</span>, on{" "}
            <span className="text-sky-400 font-semibold">real production-grade labs</span>, with one goal —{" "}
            <span className="text-sky-400 font-semibold">getting you hired</span>.
          </p>
          <div className="mt-8 rounded-2xl border border-orange-500/40 bg-gradient-to-r from-orange-500/15 to-transparent p-6">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-orange-400 font-semibold mb-2">Our Mission</p>
            <p className="font-display text-xl sm:text-2xl font-bold text-white leading-snug">
              Produce <span className="text-gradient-orange">1 Million Job-Ready Engineers</span> for the market — at a
              price every student can afford, through truly 1-on-1 personal training.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative rounded-2xl overflow-hidden border border-sky-500/25 group">
            <img
              src={IMAGES.cloud}
              alt="LearnWithTAC engineers at work"
              className="w-full h-[420px] object-cover opacity-80 group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060913] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 glass-card rounded-xl border border-sky-500/25 p-5">
              <p className="font-display text-sm font-semibold text-white">
                "We don't teach to finish a syllabus. We teach until you clear the interview."
              </p>
              <p className="mt-1.5 text-xs text-slate-400">— The LearnWithTAC Team</p>
            </div>
          </div>
        </Reveal>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
      >
        {VALUES.map(({ icon: Icon, title, desc }) => (
          <div
            key={title}
            data-testid={`about-value-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="rounded-2xl border border-sky-500/20 bg-[#0B1226] p-6 hover:border-orange-500/50 hover:-translate-y-1.5 transition-all duration-300"
          >
            <div className="w-11 h-11 rounded-xl bg-blue-600/20 border border-sky-500/30 flex items-center justify-center mb-4">
              <Icon className="w-5 h-5 text-orange-400" />
            </div>
            <h3 className="font-display text-base font-bold text-white">{title}</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</p>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);
